"use client"

import { FormikProvider, useFormik } from "formik"

import { FormSelect } from "@/components/form-inputs"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useCorrectSessionOutcomeMutation } from "@/hooks/mutations/conversation-quality"

import {
  ESCALATION_CATEGORIES,
  HUMAN_OUTCOMES,
  SESSION_OUTCOMES,
  type EscalationCategory,
  type HumanOutcome,
  type SessionOutcome,
} from "@/lib/conversation-quality/outcomes"
import { useQualityCopy } from "@/components/i18n/quality-copy-context"
import type { ThreadQuality } from "@/lib/conversation/thread-header"

/**
 * Correcting the verdict on one session.
 *
 * Which fields it needs depends on the row, not on what the operator picks here: an
 * escalated verdict needs a reason, and a human outcome only where somebody actually
 * replied. The server decides both against the stored facts — this form only stops asking
 * for what cannot apply.
 */
export function OutcomeCorrectionDialog({
  quality,
  open,
  onOpenChange,
  onCorrected,
}: {
  quality: ThreadQuality
  open: boolean
  onOpenChange: (open: boolean) => void
  onCorrected?: () => void | Promise<void>
}) {
  const { ui: tUi, OUTCOME_LABELS, ESCALATION_CATEGORY_LABELS, HUMAN_OUTCOME_LABELS } = useQualityCopy()
  const OUTCOME_OPTIONS = SESSION_OUTCOMES.map(value => ({ value, label: OUTCOME_LABELS[value] }))
  const ESCALATION_OPTIONS = ESCALATION_CATEGORIES.map(value => ({ value, label: ESCALATION_CATEGORY_LABELS[value] }))
  const HUMAN_OUTCOME_OPTIONS = HUMAN_OUTCOMES.filter(value => value !== "unhandled").map(value => ({ value, label: HUMAN_OUTCOME_LABELS[value] }))

  const correct = useCorrectSessionOutcomeMutation({
    onCorrected: async () => {
      onOpenChange(false)
      await onCorrected?.()
    },
  })

  const formik = useFormik<CorrectionValues>({
    initialValues: {
      outcome: quality.outcome,
      escalationCategory: quality.escalationCategory ?? "other",
      humanOutcome: quality.humanOutcome ?? "resolved_neutral",
    },
    // The dialog stays mounted between sessions; without this it would open on the
    // previous conversation's verdict.
    enableReinitialize: true,
    onSubmit: async (values, helpers) => {
      try {
        await correct.mutateAsync({
          sessionId: quality.sessionId,
          outcome: values.outcome,
          escalationCategory: values.outcome === "escalated" ? values.escalationCategory : null,
          humanOutcome:
            values.outcome === "escalated" && quality.humanReplied ? values.humanOutcome : null,
          setErrors: helpers.setErrors as (errors: Record<string, unknown>) => void,
        })
      } catch {
        // withQueryHandling has shown the toast and placed any field errors already.
      }
    },
  })

  const isEscalated = formik.values.outcome === "escalated"

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={formik.handleSubmit}>
          <DialogHeader>
            <DialogTitle><span key={tUi("correctOutcome")}>{tUi("correctOutcome")}</span></DialogTitle>
            <DialogDescription>
              <span key={tUi("verdictDescription", { number: quality.sessionNumber })}>{tUi("verdictDescription", { number: quality.sessionNumber })}</span>
            </DialogDescription>
          </DialogHeader>

          <FormikProvider value={formik}>
            <div className="space-y-4 py-4">
              <FormSelect name="outcome" label={tUi("howEnded")} options={OUTCOME_OPTIONS} />
              {isEscalated && (
                <FormSelect
                  name="escalationCategory"
                  label={tUi("whyCouldNotFinish")}
                  options={ESCALATION_OPTIONS}
                />
              )}
              {isEscalated && quality.humanReplied && (
                <FormSelect
                  name="humanOutcome"
                  label={tUi("humanPartEnded")}
                  options={HUMAN_OUTCOME_OPTIONS}
                />
              )}
              {isEscalated && !quality.humanReplied && (
                // Not a choice: the session holds no message from a person at all.
                <p key={tUi("unhandled")} className="text-xs text-muted-foreground">
                  {tUi("unhandled")}
                </p>
              )}
              {quality.escalated && !isEscalated && (
                <p key={tUi("handoverRecorded")} className="text-xs text-muted-foreground">
                  {tUi("handoverRecorded")}
                </p>
              )}
            </div>
          </FormikProvider>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={correct.isPending}
            >
              <span key={tUi("cancel")}>{tUi("cancel")}</span>
            </Button>
            <Button type="submit" disabled={correct.isPending}>
              <span key={tUi("save")}>{tUi("save")}</span>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface CorrectionValues {
  outcome: SessionOutcome;
  escalationCategory: EscalationCategory;
  humanOutcome: HumanOutcome;
}
